import React, { useCallback, useState } from 'react'
import styled from '@emotion/styled'
import {
	Button,
	Calendar,
	ConfigProvider,
	Form,
	Image,
	Input,
	InputNumber,
	Modal,
	Space,
	Upload,
	Typography,
	FormInstance,
	Spin,
	Cascader,
} from 'antd'
import {
	CalendarOutlined,
	CloseCircleFilled,
	LeftOutlined,
	PlusCircleOutlined,
	RightOutlined,
} from '@ant-design/icons'
import { valueType } from 'antd/es/statistic/utils'
import { HeaderRender } from 'antd/es/calendar/generateCalendar'
import locale from 'antd/es/date-picker/locale/ko_KR'
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs'
// eslint-disable-next-line import/no-extraneous-dependencies
import { CellRenderInfo } from 'rc-picker/lib/interface'
// eslint-disable-next-line import/no-extraneous-dependencies
import 'dayjs/locale/ko'

import topicExampleSrc from '../images/topic_example.png'
import titleExampleSrc from '../images/title_example.png'
import startDateExampleSrc from '../images/start_date_example.png'
import { CompanyChallengeTableItem, DescriptionImage, UpdateHotdealChallengeForm } from '../type'
import { RcFile } from 'antd/es/upload'
import { IMG_STORAGE_URL } from '@src/utils/constants'
import { TopicOption } from '../../type'

const { Title, Text } = Typography

type Props = {
	startDate: dayjs.Dayjs | null
	onStartDateChanged: (date: dayjs.Dayjs) => void
	isModificationModalOpened: boolean
	onModificationModalCanceled: () => void
	modificationModalBodyRef: React.RefObject<HTMLDivElement>
	form: FormInstance<UpdateHotdealChallengeForm>
	onFormChanged: (_: Record<string, never>, values: UpdateHotdealChallengeForm) => void
	thumbnail: string | Blob | null
	onThumbnailUploaded: (fild: RcFile) => Promise<void>
	onThumbnailRemoved: () => void
	descriptionImages: DescriptionImage[]
	onDescriptionImageUploaded: (fild: RcFile) => Promise<void>
	onDescriptionImageRemoved: (targetId: string) => void
	hasThumbnailError: boolean
	hasStartDateError: boolean
	startDateErrorMessage: string
	selectedTableItem: CompanyChallengeTableItem | undefined
	canDraftHotdealChallenge: boolean
	canCreateHotdealChallenge: boolean
	canModifyHotdealChallenge: boolean
	onDraftClicked: () => Promise<void>
	onGuidedDraftClicked: () => Promise<void>
	onCreateClicked: () => Promise<void>
	onModifyClicked: () => Promise<void>
	isDrafting: boolean
	isCreating: boolean
	isModifying: boolean
	isModificationWarnModalOpened: boolean
	onModificationModalCloseConfirmed: () => void
	onModificationWarnModalCanceled: () => void
	priceFormatter: (price: valueType | undefined) => string
	isCardRequireModalOpened: boolean
	onCardRequireModalCanceled: () => void
	topicOptions: TopicOption[]
	isEtcTopicModalOpened: boolean
	onEtcTopicModalCanceled: () => void
	selectedTopicName: string | undefined
}

export const HotdealChallengeModificationModal: React.FC<Props> = ({
	startDate,
	onStartDateChanged,
	isModificationModalOpened,
	onModificationModalCanceled,
	modificationModalBodyRef,
	form,
	onFormChanged,
	thumbnail,
	onThumbnailUploaded,
	onThumbnailRemoved,
	descriptionImages,
	onDescriptionImageUploaded,
	onDescriptionImageRemoved,
	hasThumbnailError,
	hasStartDateError,
	startDateErrorMessage,
	selectedTableItem,
	canDraftHotdealChallenge,
	canCreateHotdealChallenge,
	canModifyHotdealChallenge,
	onDraftClicked,
	onGuidedDraftClicked,
	onCreateClicked,
	onModifyClicked,
	isDrafting,
	isCreating,
	isModifying,
	isModificationWarnModalOpened,
	onModificationModalCloseConfirmed,
	onModificationWarnModalCanceled,
	priceFormatter,
	isCardRequireModalOpened,
	onCardRequireModalCanceled,
	topicOptions,
	isEtcTopicModalOpened,
	onEtcTopicModalCanceled,
	selectedTopicName,
}: Props) => {
	const [isTopicExampleShowed, setIsTopicExampleShowed] = useState(false)
	const [isTitleExampleShowed, setIsTitleExampleShowed] = useState(false)
	const [isStartDateExampleShowed, setIsStartDateExampleShowed] = useState(false)

	const [calendarMonth, setCalendarMonth] = useState(() => dayjs())

	const calendarHeaderRender: HeaderRender<dayjs.Dayjs> = useCallback(() => {
		const now = dayjs()

		return (
			<CalendarHeader>
				<Button
					size="small"
					type="text"
					shape="circle"
					icon={<LeftOutlined />}
					onClick={() =>
						setCalendarMonth((prevCalendarMonth) => prevCalendarMonth.subtract(1, 'month'))
					}
					disabled={calendarMonth.format('YYYY-MM') <= now.format('YYYY-MM')}
				/>
				<div>{calendarMonth.format('YYYY. MM')}</div>
				<Button
					size="small"
					type="text"
					shape="circle"
					icon={<RightOutlined />}
					onClick={() => setCalendarMonth((prevCalendarMonth) => prevCalendarMonth.add(1, 'month'))}
				/>
			</CalendarHeader>
		)
	}, [calendarMonth])

	const calendarCellRender: (
		date: dayjs.Dayjs,
		info: CellRenderInfo<dayjs.Dayjs>,
	) => React.ReactNode = useCallback(
		(date, info) => {
			let style: React.CSSProperties = {}

			if (startDate) {
				const twoDaysAfter = startDate.add(2, 'day')
				const eightDaysAfter = startDate.add(8, 'day')

				if (date.isSame(startDate, 'date')) {
					style = {
						...style,
						borderRadius: '24px 0 0 24px',
					}
				}

				if (
					date.isSame(startDate, 'date') ||
					(date.isAfter(startDate, 'date') && date.isBefore(twoDaysAfter, 'date'))
				) {
					style = {
						...style,
						background: '#FFE068',
					}
				}

				if (date.isSame(twoDaysAfter, 'date')) {
					style = {
						...style,
						background: 'linear-gradient(90deg, #FFE068, #FFAE12)',
					}
				}

				if (
					(date.isAfter(twoDaysAfter, 'date') && date.isBefore(eightDaysAfter, 'date')) ||
					date.isSame(eightDaysAfter, 'date')
				) {
					style = {
						...style,
						background: '#FFAE12',
					}
				}

				if (date.isSame(eightDaysAfter, 'date')) {
					style = {
						...style,
						borderRadius: '0px 24px 24px 0px',
					}
				}
			}

			const onClickDateCell = () => {
				if (!date.isSame(calendarMonth, 'month')) {
					setCalendarMonth(date)
				}

				onStartDateChanged(date)
			}

			return (
				<CalendarCellWrapper style={style} onClick={onClickDateCell}>
					{(!startDate && date.isSame(info.today, 'date')) ||
					(date.isSame(info.today, 'date') &&
						startDate &&
						(date.isBefore(startDate, 'date') || date.isAfter(startDate.add(8, 'day'), 'date'))) ? (
						<CalendarTodayCellWrapper>{date.format('DD')}</CalendarTodayCellWrapper>
					) : (
						date.format('DD')
					)}
				</CalendarCellWrapper>
			)
		},
		[startDate, onStartDateChanged, calendarMonth],
	)

	const isBeforeToday = useCallback((date: dayjs.Dayjs): boolean => {
		const now = dayjs()

		return date.format('YYYY-MM-DD') < now.format('YYYY-MM-DD')
	}, [])

	const [isNoLinkModalOpened, setIsNoLinkModalOpened] = useState(false)

	return (
		<>
			<Modal
				open={isModificationModalOpened}
				onCancel={onModificationModalCanceled}
				title={
					<Title level={3} style={{ marginLeft: '32px' }}>
						핫딜 챌린지 수정
					</Title>
				}
				footer={null}
				width={824}
				centered
				destroyOnClose
			>
				<ModificationModalBodyContainer ref={modificationModalBodyRef}>
					<Form form={form} layout="vertical" requiredMark={false} onValuesChange={onFormChanged}>
						<TopicArea id="modification-form-topic">
							<Form.Item
								name="topic"
								label={
									<TopicItemLabel
										isTopicExampleShowed={isTopicExampleShowed}
										setIsTopicExampleShowed={setIsTopicExampleShowed}
									/>
								}
								rules={[
									{
										validator(_, value) {
											if (!Array.isArray(value) || value.length < 2 || value.at(0) === 0) {
												return Promise.reject(new Error('주제 선택은 필수입니다'))
											}

											return Promise.resolve()
										},
									},
								]}
							>
								<Cascader.Panel options={topicOptions} />
							</Form.Item>
							<Text style={{ color: '#3877F7', position: 'relative', top: '-24px' }}>
								{selectedTopicName ? `선택한 챌린지 주제: ${selectedTopicName}` : ''}&nbsp;
							</Text>
						</TopicArea>

						<ThumbnailArea id="modification-form-thumbnail">
							<Form.Item label={<LabelTitle>판매 상품 (1:1 비율)</LabelTitle>}>
								<Form.Item
									name="thumbnail"
									hidden
									rules={[{ required: true, message: '대표 이미지 등록은 필수입니다' }]}
								>
									<Input />
								</Form.Item>

								{thumbnail ? (
									<ThumbnailWrapper>
										<Image
											src={
												thumbnail instanceof Blob
													? URL.createObjectURL(thumbnail)
													: `${IMG_STORAGE_URL}/hotdeal/hotdeal_product_img/${thumbnail}`
											}
											wrapperStyle={{ width: '100%', height: '100%' }}
											style={{ objectFit: 'cover', width: '100%', height: '100%' }}
										/>
										<Button
											size="small"
											type="text"
											shape="circle"
											icon={<CloseCircleFilled />}
											onClick={onThumbnailRemoved}
											style={{
												position: 'absolute',
												top: '0',
												right: '0',
												color: '#4E4E4E',
											}}
										/>
									</ThumbnailWrapper>
								) : (
									<Upload
										listType="picture-card"
										beforeUpload={onThumbnailUploaded}
										accept="image/png, image/jpeg"
										showUploadList={false}
									>
										<ThumbnailUpload />
									</Upload>
								)}

								<ThumbnailErrorWrapper>
									{hasThumbnailError ? <ErrorText>대표 이미지 등록은 필수입니다</ErrorText> : null}
								</ThumbnailErrorWrapper>
							</Form.Item>
						</ThumbnailArea>

						<DescriptionImagesArea id="modification-form-descriptionImages">
							<Form.Item label={<LabelTitle>상품 상세 페이지 (최대 10개 선택)</LabelTitle>}>
								<Form.Item name="descriptionImages" hidden>
									<Input />
								</Form.Item>

								{Array.isArray(descriptionImages) ? (
									<DescriptionImageList>
										{descriptionImages.length < 10 ? (
											<Upload
												listType="picture-card"
												beforeUpload={onDescriptionImageUploaded}
												accept="image/png, image/jpeg"
												showUploadList={false}
												multiple
											>
												<DescriptionImageUpload />
											</Upload>
										) : null}

										{descriptionImages.map((descriptionImage) => (
											<DescriptionImageWrapper key={descriptionImage.id}>
												{descriptionImage.value ? (
													<>
														<Image
															src={
																descriptionImage.value instanceof Blob
																	? URL.createObjectURL(descriptionImage.value)
																	: `${IMG_STORAGE_URL}/campaign_description_img/${descriptionImage.value}`
															}
															wrapperStyle={{ width: '100%', height: '100%' }}
															style={{ objectFit: 'cover', width: '100%', height: '100%' }}
														/>

														<Button
															size="small"
															type="text"
															shape="circle"
															icon={<CloseCircleFilled />}
															onClick={() => onDescriptionImageRemoved(descriptionImage.id)}
															style={{
																position: 'absolute',
																top: '0',
																right: '0',
																color: '#4E4E4E',
															}}
														/>
													</>
												) : (
													<Spin size="small" />
												)}
											</DescriptionImageWrapper>
										))}
									</DescriptionImageList>
								) : null}

								<DescriptionImageGuide>
									* 상품과 관련된 이미지만 첨부해 주세요.
								</DescriptionImageGuide>
							</Form.Item>
						</DescriptionImagesArea>

						<TitleArea id="modification-form-title">
							<Form.Item
								name="title"
								label={
									<TitleItemLabel
										isTitleExampleShowed={isTitleExampleShowed}
										setIsTitleExampleShowed={setIsTitleExampleShowed}
									/>
								}
								rules={[{ required: true, message: '상품 이름을 입력해 주세요' }]}
							>
								<Input placeholder="ex. 바른단백 샘플러 10개" style={{ width: '412px' }} />
							</Form.Item>
						</TitleArea>

						<LinkArea id="modification-form-link">
							<Form.Item
								name="link"
								label={<LabelTitle>제품 판매 링크 (특별 할인을 위한 비공개 링크)</LabelTitle>}
								rules={[{ required: true, message: '판매 링크를 입력해 주세요' }]}
							>
								<Input placeholder="https://" style={{ width: '412px' }} />
							</Form.Item>

							<LinkGuide>
								판매 링크가 없으신가요?
								<LinkGuideButton onClick={() => setIsNoLinkModalOpened(true)}>
									1:1문의
								</LinkGuideButton>
							</LinkGuide>
						</LinkArea>

						<BeforePriceArea id="modification-form-beforePrice">
							<Form.Item
								name="beforePrice"
								label={<LabelTitle>정상가</LabelTitle>}
								rules={[{ required: true, message: '금액을 입력해 주세요' }]}
							>
								<InputNumber
									addonAfter="원"
									formatter={priceFormatter}
									style={{ width: '412px' }}
								/>
							</Form.Item>
						</BeforePriceArea>

						<AfterPriceArea id="modification-form-afterPrice">
							<Form.Item
								name="afterPrice"
								label={<AfterPriceItemLabel />}
								rules={[{ required: true, message: '금액을 입력해 주세요' }]}
							>
								<InputNumber
									addonAfter="원"
									formatter={priceFormatter}
									style={{ width: '412px' }}
								/>
							</Form.Item>
						</AfterPriceArea>

						<StartDateArea id="modification-form-startDate">
							<Form.Item
								label={
									<StartDateItemLabel
										isStartDateExampleShowed={isStartDateExampleShowed}
										setIsStartDateExampleShowed={setIsStartDateExampleShowed}
									/>
								}
							>
								<Form.Item
									name="startDate"
									hidden
									rules={[
										{ required: true, message: '챌린지 시작일을 입력해 주세요' },
										{
											validator: (_rule, selectedStartDate: dayjs.Dayjs | null) => {
												if (selectedStartDate === null) {
													return Promise.resolve()
												}

												if (isBeforeToday(selectedStartDate)) {
													return Promise.reject(
														new Error('챌린지 시작일을 오늘 이후로 선택해주세요'),
													)
												}

												return Promise.resolve()
											},
										},
									]}
								>
									<Input />
								</Form.Item>

								<Input
									value={startDate ? startDate.format('YYYY-MM-DD') : ''}
									style={{ width: '288px' }}
									placeholder="yyyy-mm-dd"
									prefix={<CalendarOutlined />}
									status={hasStartDateError ? 'error' : ''}
								/>

								<CalendarWrapper>
									<Calendar
										style={{ width: '288px', boxShadow: '0px 4px 21px 0px rgba(0, 0, 0, 0.25)' }}
										fullscreen={false}
										headerRender={calendarHeaderRender}
										fullCellRender={calendarCellRender}
										value={calendarMonth}
										disabledDate={isBeforeToday}
										locale={locale}
									/>

									<StartDateColorGuide>
										<StartDateColorGuideItem>
											<ThreeColor />
											<StartDateColorGuideText>모집 기간(3일)</StartDateColorGuideText>
										</StartDateColorGuideItem>
										<StartDateColorGuideItem>
											<SevenColor />
											<StartDateColorGuideText>
												챌린지 인증 기간(7일) : 모집 3일째부터 7일간 인증
											</StartDateColorGuideText>
										</StartDateColorGuideItem>
										<StartDateColorGuideItem>
											<FiveColor />
											<StartDateColorGuideText>
												판매 기간(5일) : 인증 3일째부터 5일간 판매
											</StartDateColorGuideText>
										</StartDateColorGuideItem>
									</StartDateColorGuide>
								</CalendarWrapper>

								<StartDateErrorWrapper>
									{hasStartDateError ? <ErrorText>{startDateErrorMessage}</ErrorText> : null}
								</StartDateErrorWrapper>
							</Form.Item>
						</StartDateArea>

						<SellingDurationArea>
							<Form.Item label={<SellingDurationItemLabel />}>
								<Input
									value={
										startDate
											? `${startDate.add(4, 'day').format('YYYY-MM-DD')} ~ ${startDate
													.add(8, 'day')
													.format('YYYY-MM-DD')}`
											: ''
									}
									style={{ width: '288px' }}
									placeholder="yyyy-mm-dd ~ yyyy-mm-dd"
								/>
							</Form.Item>
						</SellingDurationArea>

						<ButtonArea>
							{selectedTableItem?.isTemp ? (
								<Space size="large">
									<Button
										type="default"
										onClick={onDraftClicked}
										loading={isDrafting}
										disabled={!canDraftHotdealChallenge || isCreating}
									>
										임시저장
									</Button>
									<Button
										type="primary"
										onClick={onCreateClicked}
										loading={isCreating}
										disabled={!canCreateHotdealChallenge || isDrafting}
									>
										생성하기
									</Button>
								</Space>
							) : (
								<Button
									type="primary"
									onClick={onModifyClicked}
									loading={isModifying}
									disabled={!canModifyHotdealChallenge}
								>
									수정하기
								</Button>
							)}
						</ButtonArea>
					</Form>
				</ModificationModalBodyContainer>
			</Modal>

			<Modal
				open={isEtcTopicModalOpened}
				centered
				closable={false}
				title={<EtcTopicModalHeader />}
				footer={<EtcTopicModalFooter onEtcTopicModalCanceled={onEtcTopicModalCanceled} />}
				onCancel={onEtcTopicModalCanceled}
			>
				<EtcTopicModalBody />
			</Modal>

			<Modal
				open={isNoLinkModalOpened}
				centered
				closable={false}
				title={<NoLinkModalHeader />}
				footer={<NoLinkModalFooter setIsNoLinkModalOpened={setIsNoLinkModalOpened} />}
				onCancel={() => setIsNoLinkModalOpened(false)}
			>
				<NoLinkModalBody />
			</Modal>

			<Modal
				open={isModificationWarnModalOpened}
				centered
				closable={false}
				title="페이지에서 나가시겠어요?"
				onOk={onModificationModalCloseConfirmed}
				onCancel={onModificationWarnModalCanceled}
				okText="나가기"
				cancelText="취소"
				okButtonProps={{
					danger: true,
				}}
			>
				페이지를 나가시면 수정 내역이 모두 사라집니다.
			</Modal>

			<Modal
				open={isCardRequireModalOpened}
				centered
				title="카드를 등록해야 생성이 가능해요."
				footer={
					<CardRequireModalFooter
						isDraftingCompanyChallenge={isDrafting}
						onDraftClicked={onGuidedDraftClicked}
					/>
				}
				onCancel={onCardRequireModalCanceled}
			>
				입력 내용이 사라지지 않도록 임시저장을 한 후,
				<br />
				카드등록을 진행 해 주세요.
			</Modal>
		</>
	)
}

const topicItemLabel: React.FC<{
	isTopicExampleShowed: boolean
	setIsTopicExampleShowed: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isTopicExampleShowed, setIsTopicExampleShowed }) => (
	<TopicLabel>
		<TopicLabelTitle>
			<LabelTitle>챌린지 주제를 선택해 주세요.</LabelTitle>
			<ConfigProvider
				theme={{
					token: {
						colorPrimary: '#4E4E4E',
					},
				}}
			>
				<Button
					onClick={() => setIsTopicExampleShowed((isShowed) => !isShowed)}
					type="primary"
					size="small"
				>
					예시 보기
				</Button>
			</ConfigProvider>

			{isTopicExampleShowed ? (
				<TopicExampleCard>
					<TopicExampleImg src={topicExampleSrc} />
				</TopicExampleCard>
			) : null}
		</TopicLabelTitle>
		<TopicLabelSubTitle>제품과 연관된 주제가 좋아요!</TopicLabelSubTitle>
	</TopicLabel>
)

const TopicItemLabel = React.memo(topicItemLabel)

const thumbnailUpload: React.FC = () => (
	<div>
		<PlusCircleOutlined style={{ fontSize: '24px', color: '#8B98BE' }} />
		<UploadText>560*560px</UploadText>
	</div>
)

const ThumbnailUpload = React.memo(thumbnailUpload)

const descriptionImageUpload: React.FC = () => (
	<div>
		<PlusCircleOutlined style={{ fontSize: '24px', color: '#8B98BE' }} />
	</div>
)

const DescriptionImageUpload = React.memo(descriptionImageUpload)

const titleItemLabel: React.FC<{
	isTitleExampleShowed: boolean
	setIsTitleExampleShowed: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isTitleExampleShowed, setIsTitleExampleShowed }) => (
	<TitleLabel>
		<LabelTitle>상품명</LabelTitle>
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: '#4E4E4E',
				},
			}}
		>
			<Button
				onClick={() => setIsTitleExampleShowed((isShowed) => !isShowed)}
				type="primary"
				size="small"
			>
				예시 보기
			</Button>
		</ConfigProvider>

		{isTitleExampleShowed ? (
			<TitleExampleCard>
				<TitleExampleImg src={titleExampleSrc} />
			</TitleExampleCard>
		) : null}
	</TitleLabel>
)

const TitleItemLabel = React.memo(titleItemLabel)

const afterPriceItemLabel: React.FC = () => (
	<AfterPriceLabel>
		<LabelTitle>할인가</LabelTitle>
		<AfterPriceLabelSubTitle>
			네이버, 쿠팡 최저가보다 낮게 설정해 주셔야 판매가 많이 이뤄져요.
		</AfterPriceLabelSubTitle>
	</AfterPriceLabel>
)

const AfterPriceItemLabel = React.memo(afterPriceItemLabel)

const startDateItemLabel: React.FC<{
	isStartDateExampleShowed: boolean
	setIsStartDateExampleShowed: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isStartDateExampleShowed, setIsStartDateExampleShowed }) => (
	<StartDateLabel>
		<StartDateLabelTitle>
			<LabelTitle>챌린지 시작 일 (3일 모집, 7일 인증)</LabelTitle>
			<ConfigProvider
				theme={{
					token: {
						colorPrimary: '#4E4E4E',
					},
				}}
			>
				<Button
					onClick={() => setIsStartDateExampleShowed((isShowed) => !isShowed)}
					type="primary"
					size="small"
				>
					예시 보기
				</Button>
			</ConfigProvider>

			{isStartDateExampleShowed ? (
				<StartDateExampleCard>
					<StartDateExampleImg src={startDateExampleSrc} />
				</StartDateExampleCard>
			) : null}
		</StartDateLabelTitle>
	</StartDateLabel>
)

const StartDateItemLabel = React.memo(startDateItemLabel)

const sellingDurationItemLabel: React.FC = () => (
	<SellingDurationLabel>
		<LabelTitle>제품 판매 기간</LabelTitle>
		<SellingDurationLabelSubTitle>
			챌린지 인증 기간 3일째부터 제품이 판매됩니다.
		</SellingDurationLabelSubTitle>
	</SellingDurationLabel>
)

const SellingDurationItemLabel = React.memo(sellingDurationItemLabel)

const etcTopicModalHeader: React.FC = () => (
	<ModalHeaderWrapper>
		<ModalHeaderTitle>다른 챌린지 주제를 원할 경우, 연락 주세요.</ModalHeaderTitle>
		<ModalHeaderSubTitle>(평일 10:00~18:00 까지)</ModalHeaderSubTitle>
	</ModalHeaderWrapper>
)

const EtcTopicModalHeader = React.memo(etcTopicModalHeader)

const etcTopicModalBody: React.FC = () => (
	<ModalBodyWrapper>
		<ModalBodyContact>📞 : 010-9768-2105</ModalBodyContact>
		<ModalBodyUrgentContact>📞 : (긴급 전화) 010-3454-4430</ModalBodyUrgentContact>
	</ModalBodyWrapper>
)

const EtcTopicModalBody = React.memo(etcTopicModalBody)

const etcTopicModalFooter: React.FC<{
	onEtcTopicModalCanceled: () => void
}> = ({ onEtcTopicModalCanceled }) => (
	<Button type="primary" onClick={onEtcTopicModalCanceled}>
		확인
	</Button>
)

const EtcTopicModalFooter = React.memo(etcTopicModalFooter)

const noLinkModalHeader: React.FC = () => (
	<ModalHeaderWrapper>
		<ModalHeaderTitle>1:1 문의는 아래 번호로 연락주세요.</ModalHeaderTitle>
		<ModalHeaderSubTitle>(평일 10:00~18:00 까지)</ModalHeaderSubTitle>
	</ModalHeaderWrapper>
)

const NoLinkModalHeader = React.memo(noLinkModalHeader)

const noLinkModalBody = () => (
	<ModalBodyWrapper>
		<ModalBodyContact>📞 : 010-9768-2105</ModalBodyContact>
		<ModalBodyUrgentContact>📞 : (긴급 전화) 010-3454-4430</ModalBodyUrgentContact>
	</ModalBodyWrapper>
)

const NoLinkModalBody = React.memo(noLinkModalBody)

const noLinkModalFooter: React.FC<{
	setIsNoLinkModalOpened: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ setIsNoLinkModalOpened }) => (
	<Button type="primary" onClick={() => setIsNoLinkModalOpened(false)}>
		확인
	</Button>
)

const NoLinkModalFooter = React.memo(noLinkModalFooter)

const cardRequireModalFooter: React.FC<{
	isDraftingCompanyChallenge: boolean
	onDraftClicked: () => Promise<void>
}> = ({ isDraftingCompanyChallenge, onDraftClicked }) => (
	<Button onClick={onDraftClicked} loading={isDraftingCompanyChallenge}>
		임시저장
	</Button>
)

const CardRequireModalFooter = React.memo(cardRequireModalFooter)

const LabelTitle = styled.div`
	color: #000;
	font-size: 18px;
	font-weight: 600;
`

const ErrorText = styled.div`
	color: #ff4d4f;
	font-size: 14px;
`

const TopicArea = styled.div`
	margin-top: 32px;

	& .ant-cascader-panel {
		background: #fff;
		min-height: 362px;
	}

	& .ant-cascader-panel .ant-cascader-menu {
		min-width: 148px;
	}

	& .ant-radio-group {
		max-width: 500px;
		display: flex;
		flex-wrap: wrap;
		flex-direction: row;
		gap: 18px;
	}

	& .ant-radio-button-wrapper {
		border-radius: 9px;
		border: 1px solid #d9d9d9;

		&::before {
			content: none;
		}
	}

	& .ant-form-item .ant-form-item-label {
		overflow: visible;
	}
`

const TopicLabel = styled.div`
	display: flex;
	flex-direction: column;
`

const TopicLabelTitle = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;
	position: relative;
`

const TopicExampleCard = styled.div`
	position: absolute;
	right: -296px;
	top: 0;
	z-index: 999;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0px 0px 20px rgba(191, 191, 191, 0.5);
	background: #ffffff;
`

const TopicExampleImg = styled.img`
	width: 287px;
`

const TopicLabelSubTitle = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
`

const UploadText = styled.div`
	color: #4e4e4e;
	font-size: 12px;
	font-weight: 600;
	margin-top: 4px;
`

const ThumbnailArea = styled.div`
	margin-top: 56px;

	& .ant-upload {
		margin-inline: unset !important;
		margin: unset !important;
	}
`

const ThumbnailWrapper = styled.div`
	position: relative;
	width: 100px;
	height: 100px;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid #d9d9d9;
`

const ThumbnailErrorWrapper = styled.div`
	height: 24px;
	display: flex;
	flex-direction: row;
	align-items: center;
`

const DescriptionImagesArea = styled.div`
	margin-top: 56px;

	& .ant-upload-wrapper {
		width: unset;
	}

	& .ant-upload {
		margin-inline: unset !important;
		margin: unset !important;
		width: 100px !important;
		height: 100px !important;
	}
`

const DescriptionImageList = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 16px;
`

const DescriptionImageWrapper = styled.div`
	position: relative;
	width: 100px;
	height: 100px;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid #d9d9d9;
	display: flex;
	justify-content: center;
	align-items: center;
	background: #fff;
`

const DescriptionImageGuide = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
	line-height: 24px;
	margin-top: 8px;
`

const TitleArea = styled.div`
	margin-top: 56px;

	& .ant-form-item .ant-form-item-label {
		overflow: visible;
	}
`

const TitleLabel = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;
	position: relative;
`

const TitleExampleCard = styled.div`
	position: absolute;
	right: -332px;
	top: 0;
	z-index: 999;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0px 0px 20px rgba(191, 191, 191, 0.5);
	background: #ffffff;
`

const TitleExampleImg = styled.img`
	width: 322px;
`

const BeforePriceArea = styled.div`
	margin-top: 56px;
`

const AfterPriceArea = styled.div`
	margin-top: 56px;
`

const AfterPriceLabel = styled.div`
	display: flex;
	flex-direction: column;
`

const AfterPriceLabelSubTitle = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
	line-height: 24px;
`

const LinkArea = styled.div`
	margin-top: 56px;
`

const LinkGuide = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
`

const LinkGuideButton = styled.span`
	text-decoration-line: underline;
	cursor: pointer;
	margin-left: 8px;
`

const StartDateArea = styled.div`
	margin-top: 56px;

	& .ant-picker-calendar .ant-picker-date-panel .ant-picker-content th {
		&:first-child {
			color: red;
		}
		&:last-child {
			color: blue;
		}
	}

	& .ant-form-item .ant-form-item-label {
		overflow: visible;
	}
`

const CalendarHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px;
`

const CalendarCellWrapper = styled.div`
	width: 100%;
	height: calc(100% + 12px);
	display: flex;
	justify-content: center;
	align-items: center;
`

const CalendarTodayCellWrapper = styled.div`
	height: calc(100% - 12px);
	aspect-ratio: 1 / 1;
	border-radius: 50%;
	background: #ededed;
	display: flex;
	justify-content: center;
	align-items: center;
`

const CalendarWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: 24px;
	margin-top: 8px;
`

const StartDateLabel = styled.div`
	display: flex;
	flex-direction: column;
`

const StartDateLabelTitle = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 12px;
	position: relative;
`

const StartDateExampleCard = styled.div`
	position: absolute;
	right: -334px;
	top: 0;
	z-index: 999;
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0px 0px 20px rgba(191, 191, 191, 0.5);
	padding: 12px 18px 24px;
	background: #ffffff;
`

const StartDateExampleImg = styled.img`
	width: 287px;
`

const StartDateColorGuide = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	position: relative;
	top: -60px;
`

const StartDateColorGuideItem = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;
`

const ThreeColor = styled.div`
	background: #ffdf68;
	width: 18px;
	height: 18px;
	border-radius: 50%;
`

const SevenColor = styled.div`
	background: #ffae12;
	width: 18px;
	height: 18px;
	border-radius: 50%;
`

const FiveColor = styled.div`
	background: transparent;
	width: 18px;
	height: 18px;
	border-radius: 50%;
`

const StartDateColorGuideText = styled.div`
	color: #4e4e4e;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
`

const StartDateErrorWrapper = styled.div`
	height: 24px;
	display: flex;
	flex-direction: row;
	align-items: center;
	margin-top: 8px;
`

const SellingDurationArea = styled.div`
	margin-top: -12px;
`

const SellingDurationLabel = styled.div`
	display: flex;
	flex-direction: column;
`

const SellingDurationLabelSubTitle = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
	line-height: 24px;
`

const ButtonArea = styled.div`
	margin-top: 72px;
	margin-bottom: 28px;
`

const ModificationModalBodyContainer = styled.div`
	width: 100%;
	height: calc(80vh - 72px);
	overflow: scroll;
	-ms-overflow-style: none;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
	padding: 0 32px;
`

const ModalHeaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
`

const ModalHeaderTitle = styled.div`
	color: #121212;
	font-size: 18px;
	font-weight: 600;
`

const ModalHeaderSubTitle = styled.div`
	color: #4e4e4e;
	font-size: 14px;
	font-weight: 500;
`

const ModalBodyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 24px;
`

const ModalBodyContact = styled.div`
	color: #121212;
	font-size: 14px;
	font-weight: 500;
`

const ModalBodyUrgentContact = styled.div`
	color: #910000;
	font-size: 14px;
	font-weight: 500;
`
